import { useCallback, useEffect, useState } from 'react';

interface Options<T> {
  raw?: boolean;
  initialValue?: T;
}

const DEFAULT_OPTIONS = {
  raw: false,
};

export const useSessionStorage = <T>(
  key: string,
  options: Options<T> = DEFAULT_OPTIONS
): [T | undefined, React.Dispatch<React.SetStateAction<T | undefined>>, () => void] => {
  const [state, setState] = useState<T | undefined>(() => {
    try {
      const currentValue = sessionStorage.getItem(key);
      if (typeof currentValue === 'string') {
        return options.raw ? currentValue : JSON.parse(currentValue);
      }

      return options.initialValue;
    } catch {
      return options.initialValue;
    }
  });

  const clearCurrentValue = useCallback(() => {
    setState(options.initialValue);
  }, [options.initialValue]);

  useEffect(() => {
    if (state) {
      sessionStorage.setItem(key, options.raw ? String(state) : JSON.stringify(state));
    } else {
      sessionStorage.removeItem(key);
    }
  }, [key, state, options.raw]);

  return [state, setState, clearCurrentValue];
};
